<template>
  <div class="columns1 navigation-buttons">
    <div class="flex-left">
      <button @click="previousStep" v-if="showPreviousButton" class="link-button-submit"
        :disabled="isSubmitting || isSubmitted">
        Previous
      </button>

    </div>
    <div class="flex-right">
      <button v-if="!isLastStep && !isSubmitted" class="link-button-submit" @click="nextStep" :disabled="isSubmitting">
        Next
      </button>
      <button v-if="isLastStep && !isSubmitted" class="link-button-submit" @click="submitForm" :disabled="isSubmitting">
        {{ isSubmitting ? "Submitting..." : "Submit" }}
      </button>
    </div>
  </div>
</template>



<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { submitToAPI } from "@/api/api";

/**
 * Provides the navigation buttons for a form component, handling the logic for moving between form steps, submitting the form, and disabling buttons based on the form state.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.scrollToFirstInvalidSection - A function to scroll to the first invalid form section.
 * @returns {Object} - An object containing the navigation button functionality.
 */
export default {

  props: {
    // sectionRefs: {
    //   type: Object,
    //   required: true,
    // },
    scrollToFirstInvalidSection: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();


    /**
     * Computed properties that track the state of the form, including 
     * whether the form is being submitted, 
     * the current step, 
     * the total number of steps, 
     * whether the form has been submitted, and 
     * whether the current step is the first or last step.
     *
     * @param {Object} store - The Vuex store instance.
     * @returns {Object} - An object containing the computed properties.
     */
    const isSubmitting = ref(false);
    const currentStep = computed(() => store.state.validation.currentStep);
    const totalSteps = computed(
      () => store.getters["config/getCurrentFormSteps"].length
    );
    const isSubmitted = computed(() => store.state.validation.isSubmitted);
    const isFirstStep = computed(() => currentStep.value === 1);
    const isLastStep = computed(() => currentStep.value === totalSteps.value);
    const showPreviousButton = computed(
      () => !isFirstStep.value && !isSubmitted.value && !isSubmitting.value
    );

    /**
     * Moves to the next step in the form, if the current step is valid.
     * If the current step is invalid, it scrolls to the first invalid section.
     */
    const nextStep = async () => {
      await store.dispatch("validation/setUserStepProgressAttempt", true);
      await store.dispatch("validation/validateCurrentStep");
      if (store.getters["validation/isStepValid"](currentStep.value)) {
        await store.dispatch("validation/setUserStepProgressAttempt", false);
        store.dispatch("validation/updateCurrentStep", currentStep.value + 1);
      } else {
        props.scrollToFirstInvalidSection();
      }
    };


    /**
     * Moves to the previous step in the form.
     */
    const previousStep = () => {
      store.dispatch("validation/updateCurrentStep", currentStep.value - 1);
    };


    /**
     * Submits the form to the API if all steps are valid, otherwise scrolls to the first invalid section.
     * 
     * This function is responsible for the final form submission process. It first validates the current step,
     * and if all steps are valid, it dispatches an action to set the form as submitted and show the thanks page.
     * If any step is invalid, it scrolls to the first invalid section.
     */
    const submitForm = async () => {
      // console.log("Submit form clicked");
      await store.dispatch("validation/setUserStepProgressAttempt", true);
      await store.dispatch("validation/validateCurrentStep");
      if (store.getters["validation/areAllStepsValid"]) {
        try {
          isSubmitting.value = true;
          await submitToAPI();
          const redirectUrl = store.state.config.redirectUrl;
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
          store.dispatch("validation/setSubmitted", true);
          store.dispatch("validation/showThanksPage");
        } catch (error) {
          // console.error("Error submitting form:", error);
        } finally {
          isSubmitting.value = false;
        }
      } else {
        // console.log("Form is invalid, scrolling to first invalid section");
        props.scrollToFirstInvalidSection();
      }
    };


    return {
      isSubmitting,
      isSubmitted,
      currentStep,
      totalSteps,
      isLastStep,
      showPreviousButton,
      nextStep,
      previousStep,
      submitForm,
    };
  },
};
</script>

<style scoped>
.navigation-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding: 1rem 0;
}

.flex-left,
.flex-right {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .navigation-buttons {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .flex-left,
  .flex-right {
    justify-content: center;
    margin-top: 1rem;
  }
}
</style>
